import React, { useState, useRef, useEffect } from 'react';
import { Heart, Lock, Gift, Users, TrendingUp, Zap, ArrowRight, Menu, X, Globe, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import SalesAgent from './components/SalesAgent';
import PackageGrid from './components/PackageGrid';
import FeatureMatrix from './components/FeatureMatrix';
import DemoModal from './components/DemoModal';

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const { t, i18n } = useTranslation();
  const navRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const languages = [
    { code: 'de', name: '🇩🇪 Deutsch' },
    { code: 'en', name: '🇬🇧 English' },
    { code: 'fr', name: '🇫🇷 Français' },
    { code: 'es', name: '🇪🇸 Español' },
    { code: 'it', name: '🇮🇹 Italiano' },
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white overflow-hidden">
      <Toaster position="top-right" />

      {/* Navigation */}
      <nav ref={navRef} className="fixed top-0 w-full bg-slate-900/80 backdrop-blur-md border-b border-purple-500/20 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 text-2xl font-bold">
            <span className="text-4xl">{t('hero.emoji')}</span>
            <span>{t('nav.logo')}</span>
          </div>
          
          <div className="hidden md:flex gap-8 items-center">
            <a href="#features" className="hover:text-purple-300 transition">{t('nav.features')}</a>
            <a href="#pricing" className="hover:text-purple-300 transition">{t('nav.pricing')}</a>
            <a href="#faq" className="hover:text-purple-300 transition">{t('nav.faq')}</a>
            <a href="/app/?login=1" className="text-purple-400 hover:text-purple-300">{t('nav.appLogin')}</a>
            
            {/* Language Selector */}
            <div className="relative group">
              <button className="flex items-center gap-2 bg-slate-700/50 px-3 py-2 rounded hover:bg-slate-600/50 transition">
                <Globe size={18} />
                <span className="text-sm">{i18n.language.toUpperCase()}</span>
              </button>
              <div className="absolute right-0 mt-2 bg-slate-800 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 min-w-max">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`block w-full text-left px-4 py-2 hover:bg-purple-600/50 transition ${
                      i18n.language === lang.code ? 'bg-purple-600' : ''
                    }`}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {menuOpen && (
          <div className="md:hidden bg-slate-800/95 p-4 space-y-4">
            <a href="#features" className="block hover:text-purple-300">{t('nav.features')}</a>
            <a href="#pricing" className="block hover:text-purple-300">{t('nav.pricing')}</a>
            <a href="#faq" className="block hover:text-purple-300">{t('nav.faq')}</a>
            <a href="/app/?login=1" className="block text-purple-400">{t('nav.appLogin')}</a>
            <div className="space-y-2 pt-4 border-t border-slate-700">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    changeLanguage(lang.code);
                    setMenuOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-2 rounded hover:bg-purple-600/50 transition ${
                    i18n.language === lang.code ? 'bg-purple-600' : ''
                  }`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="pt-32 pb-20 px-4 text-center"
      >
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-7xl md:text-8xl mb-6"
          >
            {t('hero.emoji')}
          </motion.div>

          <motion.p 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="inline-block px-4 py-2 rounded-full border border-purple-500/30 text-sm text-purple-300 mb-6"
          >
            {t('hero.badge')}
          </motion.p>

          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent"
          >
            {t('hero.title')}
          </motion.h1>

          <motion.p 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl"
          >
            {t('hero.description')}
          </motion.p>

          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col md:flex-row gap-4"
          >
            <a href="#pricing" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-lg flex items-center justify-center gap-2 transition-all">
              {t('hero.cta1')} <ArrowRight size={20} />
            </a>
            <button 
              onClick={() => window.location.href = '/app/?demo=demo'}
              className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-8 rounded-lg flex items-center justify-center gap-2 transition-all"
            >
              <Play size={20} /> {t('demoModal.start', { defaultValue: 'Start demo' })}
            </button>
            <a href="#vault" className="border-2 border-purple-400 hover:bg-purple-400/10 text-white font-bold py-4 px-8 rounded-lg transition-all">
              {t('hero.cta2')}
            </a>
          </motion.div>
        </div>
      </motion.section>

      {/* Short definition — crawlable by Google, Bing, and AI search tools */}
      <section id="was-ist-timelock" className="py-10 px-4 border-b border-purple-500/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-purple-300">{t('definition.title')}</h2>
          <p className="text-lg text-slate-300 leading-relaxed">{t('definition.body')}</p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-slate-800/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t('features.title')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Heart, titleKey: 'features.loved_title', descKey: 'features.loved_desc' },
              { icon: Lock, titleKey: 'features.private_title', descKey: 'features.private_desc' },
              { icon: Gift, titleKey: 'features.assets_title', descKey: 'features.assets_desc' },
              { icon: Zap, titleKey: 'features.qr_title', descKey: 'features.qr_desc' },
              { icon: TrendingUp, titleKey: 'features.planning_title', descKey: 'features.planning_desc' },
              { icon: Users, titleKey: 'features.roles_title', descKey: 'features.roles_desc' },
              { icon: Zap, titleKey: 'features.event_title', descKey: 'features.event_desc' }
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-slate-700/50 rounded-lg p-6 border border-purple-500/30 hover:border-purple-400/60 transition-all"
              >
                <feature.icon size={40} className="text-purple-400 mb-4" />
                <h3 className="text-xl font-bold mb-2">{t(feature.titleKey)}</h3>
                <p className="text-slate-300">{t(feature.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t('howItWorks.title')}</h2>

          <div className="space-y-8">
            {[
              { num: 1, titleKey: 'howItWorks.step1', descKey: 'howItWorks.step1_desc' },
              { num: 2, titleKey: 'howItWorks.step2', descKey: 'howItWorks.step2_desc' },
              { num: 3, titleKey: 'howItWorks.step3', descKey: 'howItWorks.step3_desc' },
              { num: 4, titleKey: 'howItWorks.step4', descKey: 'howItWorks.step4_desc' }
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg flex-shrink-0">
                  {step.num}
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">{t(step.titleKey)}</h3>
                  <p className="text-slate-300">{t(step.descKey)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* Use Cases — keyword-rich section for SEO + AEO */}
      <section id="anwendungsfaelle" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t('usecases.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: (i - 1) * 0.1 }}
                className="bg-slate-700/50 rounded-lg p-6 border border-purple-500/30 hover:border-purple-400/60 transition-all"
              >
                <div className="text-4xl mb-4">{t(`usecases.case${i}.emoji`)}</div>
                <h3 className="text-xl font-bold mb-3">{t(`usecases.case${i}.title`)}</h3>
                <p className="text-slate-300">{t(`usecases.case${i}.body`)}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Vault Section */}
      <section id="vault" className="py-20 px-4 bg-gradient-to-r from-amber-900/20 to-yellow-900/20 border-t border-b border-amber-500/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">{t('vault.title')}</h2>
          <p className="text-xl text-center text-slate-300 mb-16 max-w-3xl mx-auto">{t('vault.subtitle')}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
            {[
              { title: 'vault.step1_title', desc: 'vault.step1_desc', emoji: '💰' },
              { title: 'vault.step2_title', desc: 'vault.step2_desc', emoji: '🏦' },
              { title: 'vault.step3_title', desc: 'vault.step3_desc', emoji: '🔐' },
              { title: 'vault.step4_title', desc: 'vault.step4_desc', emoji: '📈' }
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-slate-700/50 border border-amber-500/30 rounded-lg p-6 text-center"
              >
                <div className="text-4xl mb-4">{step.emoji}</div>
                <h3 className="text-xl font-bold mb-3">{t(step.title)}</h3>
                <p className="text-slate-300 text-sm">{t(step.desc)}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-amber-600/20 to-yellow-600/20 border border-amber-500/30 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold mb-6 text-center">{t('vault.description')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-lg">{t(`vault.benefit${i}`)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <a href="#pricing" className="bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-white font-bold py-4 px-8 rounded-lg transition-all inline-block">
              {t('vault.cta')}
            </a>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-20 px-4 bg-gradient-to-b from-slate-800/50 to-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{t('demo.title')}</h2>
          <p className="text-xl text-slate-300 mb-12">{t('demo.subtitle')}</p>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="relative group cursor-pointer"
            onClick={() => setShowDemo(true)}
          >
            <div className="relative bg-gradient-to-br from-purple-900 to-slate-900 rounded-lg overflow-hidden border border-purple-500/30 aspect-video flex items-center justify-center group-hover:border-purple-400/60 transition-all">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/10 via-transparent to-pink-600/10"></div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="relative z-10 flex flex-col items-center gap-4"
              >
                <div className="bg-purple-600 rounded-full p-6 group-hover:bg-purple-500 transition-all">
                  <Play size={48} className="text-white fill-white" />
                </div>
                <p className="text-white font-bold text-lg">{t('demo.cta')}</p>
              </motion.div>
            </div>
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg opacity-0 group-hover:opacity-20 blur transition duration-300"></div>
          </motion.div>
        </div>
      </section>

      {/* Package Grid Section */}
      <PackageGrid />

      {/* Feature Matrix Comparison */}
      <FeatureMatrix />

      {/* Category comparison — TimeLock vs other digital planning solutions */}
      <section id="vergleich" className="py-20 px-4 bg-slate-800/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">{t('catcomp.title')}</h2>
          <p className="text-center text-slate-400 mb-12">{t('catcomp.subtitle')}</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm md:text-base">
              <thead>
                <tr className="border-b border-purple-500/30">
                  <th className="text-left py-3 px-4 text-purple-300 font-bold">{t('catcomp.col_solution')}</th>
                  <th className="text-left py-3 px-4 text-purple-300 font-bold">{t('catcomp.col_for')}</th>
                  <th className="text-left py-3 px-4 text-purple-300 font-bold hidden md:table-cell">{t('catcomp.col_limit')}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { sol: 'TimeLock', forKey: 'catcomp.timelock_for', limKey: 'catcomp.timelock_lim', highlight: true },
                  { solKey: 'catcomp.pwmanager_sol', forKey: 'catcomp.pwmanager_for', limKey: 'catcomp.pwmanager_lim' },
                  { solKey: 'catcomp.testament_sol', forKey: 'catcomp.testament_for', limKey: 'catcomp.testament_lim' },
                  { solKey: 'catcomp.cloud_sol', forKey: 'catcomp.cloud_for', limKey: 'catcomp.cloud_lim' },
                ].map((row, i) => (
                  <tr key={i} className={`border-b border-slate-700/50 ${row.highlight ? 'bg-purple-600/10' : ''}`}>
                    <td className={`py-3 px-4 font-medium ${row.highlight ? 'text-purple-300' : 'text-slate-300'}`}>
                      {row.sol || t(row.solKey)}
                    </td>
                    <td className="py-3 px-4 text-slate-300">{t(row.forKey)}</td>
                    <td className="py-3 px-4 text-slate-400 text-sm hidden md:table-cell">{t(row.limKey)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Facts block — structured AEO/GEO block readable by AI search tools */}
      <section id="fakten" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">{t('facts.title')}</h2>
          <dl className="space-y-4">
            {[
              { labelKey: 'facts.name.label', valueKey: 'facts.name.value' },
              { labelKey: 'facts.category.label', valueKey: 'facts.category.value' },
              { labelKey: 'facts.for.label', valueKey: 'facts.for.value' },
              { labelKey: 'facts.benefit.label', valueKey: 'facts.benefit.value' },
              { labelKey: 'facts.typical.label', valueKey: 'facts.typical.value' },
              { labelKey: 'facts.note.label', valueKey: 'facts.note.value' },
            ].map((row, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2 sm:gap-6 py-3 border-b border-slate-700/50">
                <dt className="text-purple-300 font-medium sm:w-48 flex-shrink-0">{t(row.labelKey)}</dt>
                <dd className="text-slate-300">{t(row.valueKey)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Demo Modal */}
      {showDemo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowDemo(false)}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden"
          >
            <button
              onClick={() => setShowDemo(false)}
              className="absolute top-4 right-4 z-10 bg-slate-900/80 hover:bg-slate-800 text-white p-2 rounded-full transition"
            >
              <X size={24} />
            </button>
            <iframe
              className="w-full h-full"
              src="/timelock-demo-v2.html"
              title="TimeLock Postcard Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </motion.div>
        </motion.div>
      )}

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">{t('faq.title')}</h2>

          <div className="space-y-4">
            {[
              { qKey: 'faq.q1', aKey: 'faq.a1' },
              { qKey: 'faq.q2', aKey: 'faq.a2' },
              { qKey: 'faq.q3', aKey: 'faq.a3' },
              { qKey: 'faq.q4', aKey: 'faq.a4' },
              { qKey: 'faq.q5', aKey: 'faq.a5' },
              { qKey: 'faq.q6', aKey: 'faq.a6' },
              { qKey: 'faq.q7', aKey: 'faq.a7' },
              { qKey: 'faq.q8', aKey: 'faq.a8' },
              { qKey: 'faq.q9', aKey: 'faq.a9' },
            ].map((faq, i) => (
              <motion.details
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                className="bg-slate-700/50 rounded-lg p-6 border border-purple-500/30 cursor-pointer group hover:border-purple-400/60 transition-all"
              >
                <summary className="font-bold text-lg flex justify-between items-center">
                  {t(faq.qKey)}
                  <span className="group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="text-slate-300 mt-4">{t(faq.aKey)}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-600 to-pink-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">{t('cta.title')}</h2>
          <p className="text-xl mb-8 text-purple-100">
            {t('cta.description')}
          </p>
          <a 
            href="/app/register"
            className="bg-white text-purple-600 hover:bg-slate-100 font-bold py-4 px-8 rounded-lg inline-flex items-center gap-2 transition-all transform hover:scale-105"
          >
            {t('cta.button')} <ArrowRight size={20} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900/50 border-t border-purple-500/20 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-lg mb-4">{t('nav.logo')}</h3>
              <p className="text-slate-400">{t('nav.logo')} - Digital Legacy Platform</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('footer.product')}</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#features" className="hover:text-purple-300">{t('nav.features')}</a></li>
                <li><a href="#pricing" className="hover:text-purple-300">{t('nav.pricing')}</a></li>
                <li><a href="#faq" className="hover:text-purple-300">{t('nav.faq')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('footer.company')}</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#" className="hover:text-purple-300">{t('footer.about')}</a></li>
                <li><a href="#" className="hover:text-purple-300">{t('footer.blog')}</a></li>
                <li><a href="#" className="hover:text-purple-300">{t('footer.contact')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">{t('footer.legal')}</h4>
              <ul className="space-y-2 text-slate-400">
                <li><a href="#" className="hover:text-purple-300">{t('footer.privacy')}</a></li>
                <li><a href="#" className="hover:text-purple-300">{t('footer.terms')}</a></li>
                <li><a href="#" className="hover:text-purple-300">{t('footer.gdpr')}</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-8 text-center text-slate-400">
            <p>{t('footer.copyright')}</p>
            <p className="mt-2 text-sm">{t('footer.tagline')}</p>
          </div>
        </div>
      </footer>

      {/* Sales Agent */}
      <SalesAgent />

      {/* Demo Modal */}
      <DemoModal isOpen={showDemo} onClose={() => setShowDemo(false)} />
    </div>
  );
}
